import { useState, useRef, useEffect } from 'react'
import { Avatar } from '../../components/index.js'
import { formatChatDate, truncate } from '../../lib/utils.js'
import type { Chat } from '../../types/chat.js'
import type { UserProfile } from '../../types/user.js'

interface ConversationCardProps {
  chat: Chat
  isActive: boolean
  users: UserProfile[]
  currentUserId: number
  lastMessagePreview?: string
  isPinned?: boolean
  canPin?: boolean
  onTogglePin?: () => void
  onClick: () => void
}

export function ConversationCard({
  chat,
  isActive,
  users,
  currentUserId,
  lastMessagePreview,
  isPinned,
  canPin,
  onTogglePin,
  onClick,
}: ConversationCardProps) {
  const displayName = getChatDisplayName(chat, users, currentUserId)
  const [showMenu, setShowMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close context menu on outside click
  useEffect(() => {
    if (!showMenu) return
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showMenu])

  function handleContextMenu(e: React.MouseEvent) {
    e.preventDefault()
    setShowMenu(true)
  }

  return (
    <div className="relative">
      <button
        onClick={onClick}
        onContextMenu={handleContextMenu}
        className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${
          isActive
            ? 'bg-(--color-primary-light) dark:bg-(--color-primary)/10'
            : 'hover:bg-(--color-bg-tertiary)'
        }`}
      >
        <Avatar name={displayName} size="md" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1 truncate text-sm font-medium text-(--color-text-primary)">
              {chat.type === 'group' && (
                <svg className="h-3.5 w-3.5 shrink-0 text-(--color-text-muted)" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>
              )}
              <span className="truncate">{displayName}</span>
            </span>
            <span className="ml-2 shrink-0 text-xs text-(--color-text-muted)">
              {formatChatDate(chat.updatedAt)}
            </span>
          </div>
          {lastMessagePreview && (
            <p className="mt-0.5 truncate text-xs text-(--color-text-muted)">
              {truncate(lastMessagePreview, 60)}
            </p>
          )}
        </div>
      </button>

      {/* Context menu */}
      {showMenu && (
        <div
          ref={menuRef}
          className="absolute right-2 top-2 z-50 w-40 rounded-lg border border-(--color-border) bg-(--color-bg-primary) py-1 shadow-lg"
        >
          {(isPinned || canPin) && (
            <button
              onClick={() => {
                setShowMenu(false)
                onTogglePin?.()
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-xs text-(--color-text-primary) hover:bg-(--color-bg-tertiary) transition-colors"
            >
              <svg className="h-3.5 w-3.5 text-(--color-text-muted)" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
              </svg>
              {isPinned ? 'Desafixar' : 'Fixar no topo'}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

function getChatDisplayName(chat: Chat, users: UserProfile[], currentUserId: number): string {
  if (chat.type === 'group') return chat.name ?? 'Grupo sem nome'
  const otherMember = chat.members?.find((m) => m.userId !== currentUserId)
  if (otherMember) {
    const otherUser = users.find((u) => u.id === otherMember.userId)
    if (otherUser) return otherUser.name
  }
  return chat.name ?? 'Conversa direta'
}
